/**
 * The surveys a saved AI crosstab was built on (M4S-5646, BT-2062).
 *
 * Until this, an AI crosstab saved its rows, banner and settings but not its surveys, so the
 * Crosstabs grid and list reopened every one of them on every survey of its project: a 422 past
 * 150 questions, or quietly more waves than it was built on. Spec:
 * `docs/pdlc/crosstabs-saved-survey-scope.md` §6.
 *
 * The scope is record state inside `ai_layout.state` (`surveyScope`), not a setting, and it is
 * stored only for a crosstab that is on something other than its whole project. It is stamped with
 * the record's own key, because the grid's Duplicate copies `ai_layout` verbatim and a copy must not
 * pass for having been saved on its source's surveys.
 *
 * Pure, and safe in the `'use client'` workspace bundle: a type-only import from `./ai-layout` and
 * nothing that reaches the database or a route.
 */

import type { AiCrosstabState } from './ai-layout';
import { isOutsideSourceId } from './outside-dataset';

/**
 * The most surveys a scope may hold. Equal to `MAX_PROJECT_SURVEYS` in `project-data.ts` (a test
 * pins the two together): a longer list would be trimmed by the data route anyway.
 */
export const MAX_SAVED_SURVEYS = 250;

/** A record key as the portal mints them. */
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `ai_layout.state.surveyScope`: the surveys a crosstab is on, stamped with the key that saved it. */
export interface SurveyScope {
    layoutKey: string;
    surveyIds: string[];
}

/** Where the data route's first load of a saved crosstab took its surveys from. */
export type ScopeSource = 'saved' | 'inferred' | 'unread';

/**
 * Reads a list of survey ids from anything. Keeps trimmed digit strings and non-negative safe
 * integers (as strings), first occurrence first, and drops everything else. A list longer than
 * `MAX_SAVED_SURVEYS` reads as none, because no part of it can be trusted to be the whole.
 */
export function parseSurveyIds(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    const out: string[] = [];
    const seen = new Set<string>();
    for (const item of value) {
        let id: string | null = null;
        if (typeof item === 'string') {
            const trimmed = item.trim();
            if (/^\d+$/.test(trimmed)) id = trimmed;
            // M4S-5777 X120: an outside dataset (`ext:<uuid>`) is one more source of the crosstab;
            // the data route reads it from the dataset store, never survey_portal.
            else if (isOutsideSourceId(trimmed)) id = trimmed.toLowerCase();
        } else if (typeof item === 'number' && Number.isSafeInteger(item) && item >= 0) {
            id = String(item);
        }
        if (id === null || seen.has(id)) continue;
        seen.add(id);
        out.push(id);
    }
    return out.length > MAX_SAVED_SURVEYS ? [] : out;
}

/** Whether two lists name the same surveys in the same order. */
export function sameSurveyList(a: readonly string[], b: readonly string[]): boolean {
    return a.length === b.length && a.every((id, i) => id === b[i]);
}

/** Whether two lists name the same surveys, in any order. */
export function sameSurveySet(a: readonly string[], b: readonly string[]): boolean {
    if (a.length !== b.length) return false;
    const set = new Set(a);
    return b.every((id) => set.has(id));
}

/** Reads a stored scope, or null when it is absent or malformed. */
export function parseSurveyScope(value: unknown): SurveyScope | null {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
    const { layoutKey, surveyIds } = value as Record<string, unknown>;
    if (typeof layoutKey !== 'string' || !UUID_RE.test(layoutKey)) return null;
    const ids = parseSurveyIds(surveyIds);
    if (ids.length === 0) return null;
    return { layoutKey: layoutKey.toLowerCase(), surveyIds: ids };
}

/** The scope a save stores, or undefined when there is nothing to store. */
export function buildSurveyScope(layoutKey: unknown, surveyIds: unknown): SurveyScope | undefined {
    if (typeof layoutKey !== 'string' || !UUID_RE.test(layoutKey)) return undefined;
    const ids = parseSurveyIds(surveyIds);
    if (ids.length === 0) return undefined;
    return { layoutKey: layoutKey.toLowerCase(), surveyIds: ids };
}

/** The stamp a record's state carries, whoever stamped it. */
export function stampOf(state: unknown): SurveyScope | null {
    if (typeof state !== 'object' || state === null || Array.isArray(state)) return null;
    return parseSurveyScope((state as AiCrosstabState).surveyScope);
}

/** The surveys a record was saved on, but only when the stamp is its own; otherwise `[]`. */
export function ownSavedSurveyIds(state: unknown, recordKey: string): string[] {
    const stamp = stampOf(state);
    if (!stamp || typeof recordKey !== 'string') return [];
    return stamp.layoutKey === recordKey.toLowerCase() ? stamp.surveyIds : [];
}

/** The synthetic survey column a Brand Lift exposed / control variable or a nest layer reads. */
export const SURVEY_COLUMN = '__SURVEY_ID';
/** A wave variant of a question, which exists only in a multi-wave load. */
const VARIANT_RE = /__V\d+$/;
/** How deep `requiredQuestionIds` walks a variable before giving up on it. */
const MAX_WALK_DEPTH = 24;

const isObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

/**
 * Visits every field of every object inside a stored variable, in order, however it is nested, at
 * most `MAX_WALK_DEPTH` levels down. The one walker for reading a variable from an untrusted
 * `ai_layout`: the question ids it uses (`requiredQuestionIds`) and the surveys it names
 * (`surveyColumnValues`).
 */
export function walkVariable(
    value: unknown,
    visit: (key: string, child: unknown, node: Record<string, unknown>) => void,
    depth = 0,
): void {
    if (depth > MAX_WALK_DEPTH) return;
    if (Array.isArray(value)) {
        for (const item of value) walkVariable(item, visit, depth + 1);
        return;
    }
    if (!isObject(value)) return;
    for (const [key, child] of Object.entries(value)) {
        visit(key, child, value);
        if (isObject(child)) walkVariable(child, visit, depth + 1);
    }
}

/** Collects every `questionId` / `sourceQuestionId` string in a variable, however it is nested. */
function collectVariableRefs(value: unknown, into: string[]): void {
    walkVariable(value, (key, child) => {
        if ((key === 'questionId' || key === 'sourceQuestionId') && typeof child === 'string') {
            into.push(child);
        }
    });
}

/**
 * The surveys one variable selects by the survey column: the values of its `__SURVEY_ID` "is"
 * conditions (a Brand Lift Exposed / Unexposed variable built from survey membership, or a nest
 * layer over surveys), first one first. A "not" condition selects everyone else, so it names no
 * survey here. Never throws.
 */
export function surveyColumnValues(variable: unknown): string[] {
    const values: unknown[] = [];
    try {
        walkVariable(variable, (key, child, node) => {
            if (
                key === 'questionId' &&
                typeof child === 'string' &&
                child.trim().toUpperCase() === SURVEY_COLUMN &&
                (node.operator === undefined || node.operator === 'eq')
            ) {
                values.push(node.value);
            }
        });
    } catch {
        return [];
    }
    return parseSurveyIds(values);
}

/**
 * The question ids a saved crosstab uses, upper-cased, for guessing the survey it was built on.
 *
 * Never throws: `buildAiLayout` leaves empty collections out, so a stored state may have no
 * `customVariables` or `liveFilters` key at all (TMRE 2026 has neither), and the column is
 * untrusted. Variables are not questions, so every variable id is removed.
 *
 * `refuse` says the crosstab cannot be guessed at all: it reads the survey column (a Brand Lift
 * exposed / control variable or a nest layer), it names a wave variant (`__vN`, which exists only
 * when several waves were loaded together), or it uses nothing.
 */
export function requiredQuestionIds(state: unknown): { ids: string[]; refuse: boolean } {
    try {
        if (!isObject(state) || Array.isArray(state)) return { ids: [], refuse: true };
        const raw: string[] = [];
        for (const key of ['activeRows', 'activeBanner'] as const) {
            const list = state[key];
            if (Array.isArray(list)) {
                for (const id of list) if (typeof id === 'string') raw.push(id);
            }
        }
        const filters = state.liveFilters;
        if (Array.isArray(filters)) {
            for (const f of filters) {
                if (isObject(f) && typeof f.questionId === 'string') raw.push(f.questionId);
            }
        }

        const variables = Array.isArray(state.customVariables) ? state.customVariables : [];
        const variableIds = new Set<string>();
        let readsSurveyColumn = false;
        for (const v of variables) {
            if (!isObject(v)) continue;
            if (typeof v.id === 'string' && v.id.trim()) {
                variableIds.add(v.id.trim().toUpperCase());
            }
            collectVariableRefs(v, raw);
            try {
                if (JSON.stringify(v).toUpperCase().includes(SURVEY_COLUMN)) {
                    readsSurveyColumn = true;
                }
            } catch {
                readsSurveyColumn = true;
            }
        }

        const ids: string[] = [];
        const seen = new Set<string>();
        for (const id of raw) {
            const upper = id.trim().toUpperCase();
            if (!upper || variableIds.has(upper) || seen.has(upper)) continue;
            seen.add(upper);
            ids.push(upper);
        }

        const refuse =
            readsSurveyColumn ||
            ids.length === 0 ||
            ids.some((id) => id === SURVEY_COLUMN || VARIANT_RE.test(id));
        return { ids, refuse };
    } catch {
        return { ids: [], refuse: true };
    }
}

/**
 * The one survey a crosstab could have been built on: the only candidate that holds every question
 * it uses. Null on a refusal, on a question no candidate holds, and on two or more holders. There is
 * no tiebreak, because a tiebreak would be a guess between waves.
 *
 * `holdings` maps `String(surveyId)` to the upper-cased question ids that survey has.
 */
export function inferBuildSurvey({
    required,
    holdings,
    candidates,
}: {
    required: { ids: string[]; refuse: boolean };
    holdings: ReadonlyMap<string, ReadonlySet<string>>;
    candidates: readonly string[];
}): string | null {
    if (required.refuse || required.ids.length === 0) return null;
    const holders = [...new Set(candidates.map(String))].filter((id) => {
        const held = holdings.get(id);
        return !!held && required.ids.every((qid) => held.has(qid));
    });
    return holders.length === 1 ? holders[0] : null;
}

/** What the data route said about this session's first load. */
export interface FirstLoad {
    surveyIds: string[];
    scopeSource?: ScopeSource;
    wholeProject?: boolean;
}

export type SaveSession =
    /** A create or a copy: a new record. */
    | 'create'
    /** The project row's lookup found no crosstab, so this session creates one for "this project". */
    | 'projectRowCreate'
    /** An existing record, opened by key or found by the project row's lookup. */
    | 'opened';

/**
 * The `surveyIds` a save carries (spec §6.1 R5), or undefined to store no scope.
 *
 * "On screen" is the first load's surveys plus any survey added with + Add survey that is checked,
 * less any first-load survey the person unchecked (`removed`, M4S-5777 X115). Nothing else ever
 * narrows it: a refetch that came back smaller cannot, because only the person's own unticking is
 * in `removed`. An opened record whose own saved list did not load (the record read failed, or the
 * project row opened it on another list) carries that list unchanged.
 */
export function scopeForSave({
    session,
    ownSaved,
    firstLoad,
    loaded,
    checked,
    confirmed,
    removed,
}: {
    session: SaveSession;
    ownSaved: readonly string[];
    firstLoad: FirstLoad | null;
    loaded: readonly string[];
    checked: readonly string[];
    /**
     * M4S-5777: the surveys the user said this crosstab is on, from the "cannot tell" notice
     * (`shouldAskForScope`). Honoured only where nothing better is known: an opened record with no
     * stamp of its own, or a copy of one. Ignored for a stamped record and for the project row's
     * create. An answer naming nothing that loaded is no answer. Once answered, the scope follows
     * the live Data source checkboxes (checked and loaded), so a survey re-checked after the answer
     * is saved with the table it is on; the answer itself is the fallback only when nothing loaded
     * is checked.
     */
    confirmed?: readonly string[];
    /**
     * M4S-5777 X115: the first-load surveys the person unticked on the Data source chips. Only
     * the rail's own setter records them, never a load, so a smaller refetch is never a removal.
     * One the person ticked again is not removed (it is in `checked`).
     */
    removed?: readonly string[];
}): string[] | undefined {
    if (!firstLoad) return session === 'opened' && ownSaved.length ? [...ownSaved] : undefined;

    const first = parseSurveyIds(firstLoad.surveyIds);
    const firstSet = new Set(first);
    const checkedSet = new Set(checked);
    const added = parseSurveyIds(loaded).filter((id) => !firstSet.has(id) && checkedSet.has(id));
    // X115: a survey the person unticked leaves what is on screen. Never all of them: the rail
    // keeps one ticked, and an empty answer here would read as "no scope", the whole project.
    const removedNow = new Set(
        parseSurveyIds(removed ?? []).filter((id) => firstSet.has(id) && !checkedSet.has(id)),
    );
    const kept0 = parseSurveyIds([...first, ...added]);
    const narrowed = kept0.filter((id) => !removedNow.has(id));
    const dropped = narrowed.length > 0 && narrowed.length < kept0.length;
    const onScreen = dropped ? narrowed : kept0;
    const stored = (ids: string[]) => (ids.length ? ids : undefined);
    const present = new Set([...first, ...parseSurveyIds(loaded)]);
    const kept = parseSurveyIds(confirmed ?? []).filter((id) => present.has(id));
    const checkedPresent = parseSurveyIds([...first, ...parseSurveyIds(loaded)]).filter((id) =>
        checkedSet.has(id),
    );
    const confirmedScope = !kept.length
        ? []
        : checkedPresent.length
          ? checkedPresent
          : parseSurveyIds([...kept, ...added]);

    switch (session) {
        case 'create':
            if (confirmedScope.length) return confirmedScope;
            return firstLoad.wholeProject === false || added.length || dropped
                ? stored(onScreen)
                : undefined;
        case 'projectRowCreate':
            return added.length || dropped ? stored(onScreen) : undefined;
        case 'opened':
            if (ownSaved.length) {
                return sameSurveySet(first, ownSaved) ? stored(onScreen) : [...ownSaved];
            }
            if (confirmedScope.length) return confirmedScope;
            return firstLoad.scopeSource === 'saved' ||
                firstLoad.scopeSource === 'inferred' ||
                added.length ||
                dropped
                ? stored(onScreen)
                : undefined;
    }
}

// ---------------------------------------------------------------------------
// M4S-5777 (defect 9): a crosstab saved before its surveys were saved
// ---------------------------------------------------------------------------

/**
 * When AI crosstabs began storing their surveys in production: M4S-5646 reached `main` in the
 * Adirondack release (#857, merged 2026-10-07 22:19 UTC) and was deployed after that. The instant
 * is set the next morning on purpose. A record made by the old code during the deploy reads as made
 * before (one extra question), where a cutoff too early would let one reopen on every survey with
 * nothing said. Spec: docs/pdlc/crosstabs-survey-load.md §4.2.
 */
export const SCOPE_SAVED_SINCE = '2026-10-08T12:00:00Z';

/**
 * Whether a record could have been saved on part of its project without saying so: it has no stamp
 * of its own, and it was created before `SCOPE_SAVED_SINCE`. A creation time that is missing or
 * unreadable reads as before, because a needless question is harmless and a silent over-count is
 * not. A record made since then with no stamp is a whole-project crosstab by rule (it would have
 * stored its surveys otherwise), so it is never asked about.
 */
export function isPreScopeRecord({
    ownSaved,
    createdAt,
}: {
    ownSaved: readonly string[];
    createdAt: unknown;
}): boolean {
    if (parseSurveyIds(ownSaved).length > 0) return false;
    let at = Number.NaN;
    if (createdAt instanceof Date) at = createdAt.getTime();
    else if (typeof createdAt === 'string' && createdAt.trim()) at = Date.parse(createdAt);
    else if (typeof createdAt === 'number') at = createdAt;
    if (!Number.isFinite(at)) return true;
    return at < Date.parse(SCOPE_SAVED_SINCE);
}

/** A wave variant id (`Q5__V2`) holds its base question too: a filter on the base means every wave. */
const variantBase = (qid: string) => qid.replace(VARIANT_RE, '');

/**
 * Which questions each loaded survey holds, read from the catalog the data route already sent
 * (each question's `surveyIds`), upper-cased. A wave variant counts as its base question as well.
 */
export function holdingsFromCatalog(questions: unknown): Map<string, Set<string>> {
    const holdings = new Map<string, Set<string>>();
    for (const q of Array.isArray(questions) ? questions : []) {
        if (!isObject(q) || typeof q.qid !== 'string' || !q.qid.trim()) continue;
        const qid = q.qid.trim().toUpperCase();
        for (const sid of parseSurveyIds(q.surveyIds)) {
            const held = holdings.get(sid) ?? new Set<string>();
            held.add(qid);
            held.add(variantBase(qid));
            holdings.set(sid, held);
        }
    }
    return holdings;
}

/**
 * The surveys an unstamped crosstab was most likely built on, among those that loaded, or `[]`
 * when nothing points at a subset. Two signals, in order, and no third:
 *
 * 1. The surveys its variables select by the survey column (a Brand Lift Exposed / Unexposed pair),
 *    among those that loaded. When that is every loaded survey, the suggestion is all of them,
 *    which `shouldAskForScope` reads as nothing to ask (decision SS3).
 * 2. Otherwise the one loaded survey that holds every question it uses (`inferBuildSurvey`, the
 *    same rule the data route guesses with past the question budget).
 *
 * A suggestion is only ever offered to the user; nothing is applied or saved on it alone.
 */
export function suggestBuildSurveys({
    state,
    holdings,
    loaded,
}: {
    state: unknown;
    holdings: ReadonlyMap<string, ReadonlySet<string>>;
    loaded: readonly string[];
}): string[] {
    try {
        const loadedIds = parseSurveyIds(loaded);
        const variables =
            isObject(state) && Array.isArray(state.customVariables) ? state.customVariables : [];
        const named = new Set(variables.flatMap((v) => surveyColumnValues(v)));
        const fromVariables = loadedIds.filter((id) => named.has(id));
        if (fromVariables.length > 0) return fromVariables;
        const one = inferBuildSurvey({
            required: requiredQuestionIds(state),
            holdings,
            candidates: loadedIds,
        });
        return one ? [one] : [];
    } catch {
        return [];
    }
}

/**
 * Whether to ask which surveys an opened crosstab is on (spec §4.2). Only when the record may have
 * been saved on part of its project (`isPreScopeRecord`), the first load did not take its surveys
 * from the record (`scopeSource` is unset), two or more surveys loaded, and the suggestion is not
 * simply every loaded survey (decision SS3: then there is nothing to choose).
 */
export function shouldAskForScope({
    preScope,
    scopeSource,
    loaded,
    suggestion,
}: {
    preScope: boolean;
    scopeSource: ScopeSource | undefined;
    loaded: readonly string[];
    suggestion: readonly string[];
}): boolean {
    if (!preScope || scopeSource !== undefined) return false;
    const ids = parseSurveyIds(loaded);
    if (ids.length < 2) return false;
    return !(suggestion.length > 0 && sameSurveySet(parseSurveyIds(suggestion), ids));
}
